import { createContext, useContext, type ReactNode } from 'react';
import { createShareHistory, ProjectSharingClient, type ShareHistory } from './client';
import { assertProjectSharePrivacy } from './privacy';
import { readSharedProjectFile } from './format';

export interface ProjectSharingServices {
  enabled: boolean;
  client: Pick<
    ProjectSharingClient,
    'reserve' | 'upload' | 'metadata' | 'download' | 'revoke' | 'capabilities'
  >;
  history: ShareHistory;
  link(id: string): string;
  copyLink(id: string): Promise<void>;
  assertPrivacy: typeof assertProjectSharePrivacy;
  decode: typeof readSharedProjectFile;
}
const Scope = createContext<ProjectSharingServices | null>(null);
export function ProjectSharingProvider({
  services,
  children,
}: {
  services: ProjectSharingServices;
  children: ReactNode;
}) {
  return <Scope.Provider value={services}>{children}</Scope.Provider>;
}
export function useOptionalProjectSharing() {
  return useContext(Scope);
}
export function useProjectSharing() {
  const services = useOptionalProjectSharing();
  if (!services) throw new Error('Project sharing services were not provided.');
  return services;
}

/** Composition boundary: browser capabilities are bound here, never inside route components. */
export function createBrowserProjectSharingServices(): ProjectSharingServices {
  const endpoint = (
    import.meta.env.VITE_PROJECT_SHARING_URL as string | undefined
  )?.replace(/\/$/, '');
  const history = createShareHistory({
    storage: {
      getItem: (key) => localStorage.getItem(key),
      setItem: (key, value) => localStorage.setItem(key, value),
    },
    requestId: () => crypto.randomUUID(),
    managementSecret: () =>
      Array.from(crypto.getRandomValues(new Uint8Array(32)), (byte) =>
        byte.toString(16).padStart(2, '0'),
      ).join(''),
  });
  const origin = location.origin;
  const link = (id: string) => `${origin}/share/${id}`;
  return {
    enabled: !!endpoint,
    client: new ProjectSharingClient(
      endpoint,
      {
        fetch: (input, init) => fetch(input, init),
        createXHR: () => new XMLHttpRequest(),
      },
      history,
      async () => {
        const { registrySession } = await import('@/account/runtime');
        return registrySession();
      },
    ),
    history,
    link,
    copyLink: async (id) => {
      if (!navigator.clipboard) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(link(id));
    },
    assertPrivacy: assertProjectSharePrivacy,
    decode: readSharedProjectFile,
  };
}
